import styled, { css } from 'styled-components';
import theme from '../../styles/theme';

export interface InputProps {
  id?: string;
  name?: string;
  value?: string;
  label?: string;
  type?: 'text' | 'number' | 'password' | 'check' | 'email';
  size?: 'xs' | 'sm' | 'md' | 'lg';
  status?: 'normal' | 'error';
  readOnly?: boolean;
  placeholder?: string;
  onChange?: (e) => any;
  onBlur?: (e) => any;
  hasFocus?: boolean;
  onKeydown?: () => any;
  placeHolder?: string;
  message?: string;
  disabled?: boolean;
  maxLength?: number;
  autocomplete?: string;
  onlyNumbers?: boolean;
  showPasswordButton?: boolean;
  onClick?: () => any;
  inputRef?: any;
  maxLengthHelpText?: boolean;
  pattern?: string;
  icons?: {
    iconLeft?: any;
    iconRight?: any;
  };
  variant?: 'transparent';
  modifier?: 'weak';
  $hasFilePlaceholder?: boolean;
}

export const Input = styled.div`
  width: 100%;
  position: relative;
  input:-webkit-autofill,
  input:-webkit-autofill:hover,
  input:-webkit-autofill:focus,
  input:-webkit-autofill:active {
    -webkit-transition: 'color 9999s ease-out, background-color 9999s ease-out';
    -webkit-transition-delay: 9999s;
  }
`;

export const LabelWrapper = styled.div`
  margin-bottom: ${theme.spacing['spacing-2']};
`;

export const BaseInput = styled.div<InputProps>`
  margin-top: ${theme.spacing['spacing-2']}
  background-color: ${theme.colors.neutral.bg.inverted};
  border-radius: ${theme.borderRadius['border-radius-3']};
  padding: ${theme.spacing['spacing-2']};
  display: flex;
  align-items: center;
  
  ${theme.textStyles['text-preset-9']};

  &:focus,
  &:focus-visible {
    outline: none;
  }

  outline: none;

  &::-moz-focus-inner {
    border: 0;
  }

  input {
    align-content: center;
    font-size:16px;
  }

  input[type="file"]::-webkit-file-upload-button, input[type="file"]::file-selector-button {
    display: none;
  }

  input[type="file"]{
    cursor: pointer;
  }

  ${({ $hasFilePlaceholder }) =>
    $hasFilePlaceholder &&
    css`
      input[type='file'] {
        height: 34px;
        color: transparent;
      }
    `}

  ${({ $hasFilePlaceholder }) =>
    !$hasFilePlaceholder &&
    css`
      input[type='file'] {
        height: 80px;
      }
    `}

  input::placeholder {
    color:${theme.colors['neutral'].text.weak};
  }

  ${({ size }) => getSize(size)}
  ${({ hasFocus, variant }) => css`
    box-shadow: ${variant === 'transparent'
      ? 'none'
      : `inset 0 0 0 1px ${theme.colors[`${hasFocus ? 'secondary-1' : 'neutral'}`].border.regular}`};
    padding: ${variant === 'transparent' ? 0 : theme.spacing['spacing-2']};
    input {
      caret-color: ${theme.colors['secondary-1'].border.regular};
    }
  `}

  ${({ status }) =>
    status === 'error' &&
    css`
      box-shadow: inset 0 0 0 1px ${theme.colors['danger'].border.regular};
      input {
        caret-color: ${theme.colors['danger'].border.regular};
        color: ${theme.colors['danger'].text.regular};
      }
    `}
  ${({ disabled, variant }) =>
    disabled === true &&
    variant !== 'transparent' &&
    css`
      cursor: not-allowed;
      input {
        color: ${theme.colors['neutral'].text.weak};
        background-color: ${theme.colors['neutral'].bg.regular};
        cursor: not-allowed;

        &::placeholder {
          color: ${theme.colors['neutral'].text.weak};
          opacity: 1;
        }
      }
      box-shadow: inset 0 0 0 1px ${theme.colors['neutral'].border.weak};
      background-color: ${theme.colors['neutral'].bg.regular};
      &:hover,
      &:focus {
        box-shadow: inset 0 0 0 1px ${theme.colors['neutral'].border.weak};
      }
    `}

    

  ${({ readOnly }) =>
    readOnly === true &&
    css`
      box-shadow: inset 0 0 0 1px ${theme.colors['neutral'].border.weak};
      input {
        color: ${theme.colors['neutral'].text.regular};
      }
      &:hover,
      &:focus {
        box-shadow: inset 0 0 0 1px ${theme.colors['neutral'].border.weak};
      }
    `}

  ${({ modifier, readOnly }) =>
    modifier === 'weak' && readOnly === true &&
    css`
      box-shadow: inset 0 0 0 1px ${theme.colors['neutral'].border.weak};
      input {
        color: ${theme.colors['neutral'].text.weak};
      }
      &:hover,
      &:focus {
        box-shadow: inset 0 0 0 1px ${theme.colors['neutral'].border.weak};
      }
    `}
  ${({ onlyNumbers }) =>
    onlyNumbers === true &&
    css`
      input::-webkit-outer-spin-button,
      input::-webkit-inner-spin-button {
        -webkit-appearance: none;
        margin: 0;
      }

      input[type='number'] {
        -moz-appearance: textfield;
      }
    `}
    ${({ variant }) =>
      variant === 'transparent' &&
      css`
        input {
          margin-bottom: 1px;
          padding: 0;
          font-size: 20px;
          color: ${theme.colors.primary.text.regular};
          font-weight: bold;
          background-color: transparent;
          border: none;
          box-shadow: none;
          &:focus {
            background-color: transparent;
            box-shadow: none;
          }
          &::placeholder {
            color: ${theme.colors.primary.text.regular};
            font-weight: bold;
          }
        }
      `}
`;

export const InputTag = styled.input`
  height: 34px !important;
  width: 100%;
  border: none;
  background-color: ${theme.colors.neutral.bg.inverted};
  color: ${theme.colors.neutral.text.strong};
  &:focus,
  &:focus-visible {
    outline: none;
  }

  outline: none;

  &::-moz-focus-inner {
    border: 0;
  }

  * + & {
    margin-left: ${theme.spacing['spacing-0']};
  }

  & + * {
    margin-left: ${theme.spacing['spacing-0']};
  }
`;

export const HelpText = styled.div<InputProps>`
  padding: ${theme.spacing['spacing-2']} ${theme.spacing['spacing-2']}
    ${theme.spacing['spacing-2']} ${theme.spacing['spacing-2']};
  display: flex;
  align-items: flex-start;
  position: absolute;
  ${({ status }) =>
    status === 'error' &&
    css`
      > * {
        &:first-of-type {
          margin-right: ${theme.spacing['spacing-1']};
        }
      }
    `}
`;

export const SiblingInputTag = styled.div`
  height: 24px;
  display: flex;
  align-items: center;
`;

export const FileInputWrapper = styled.div`
  position: relative;
  width: 100%;
`;

export const FileInputPlaceholder = styled.span<{ hasFile?: boolean }>`
  position: absolute;
  left: ${theme.spacing['spacing-2']};
  top: 50%;
  transform: translateY(-50%);
  pointer-events: none;
  color: ${({ hasFile }) =>
    hasFile
      ? theme.colors.neutral.text.strong
      : theme.colors.neutral.text.weak};
  ${theme.textStyles['text-preset-9']};
  max-width: calc(100% - 40px);
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const getSize = (size) => {
  switch (size) {
    case 'xs':
      return css`
        width: 35px;
      `;
    case 'sm':
      return css`
        width: 215px;
      `;
    case 'md':
      return css`
        width: auto;
      `;
    case 'lg':
      return css`
        width: 560px;
      `;
  }
};
